import { apiClient } from './apiClient';

export interface BenchmarkReport {
  slug: string;
  filename: string;
  size_bytes: number;
  is_summary: boolean;
}

export interface BenchmarkRun {
  run_id: string;
  timestamp_utc: string;
  report_count: number;
  has_summary: boolean;
  reports: BenchmarkReport[];
}

export interface RunListResponse {
  runs: BenchmarkRun[];
  total: number;
}

export interface ReportContentResponse {
  run_id: string;
  slug: string;
  filename: string;
  markdown: string;
  size_bytes: number;
}

export const llmBenchmarkService = {
  async listRuns(): Promise<RunListResponse> {
    const { data } = await apiClient.get<RunListResponse>('/auth/admin/llm-benchmarks');
    return data;
  },

  async getRun(runId: string): Promise<BenchmarkRun> {
    const { data } = await apiClient.get<BenchmarkRun>(`/auth/admin/llm-benchmarks/${runId}`);
    return data;
  },

  async getReport(runId: string, slug: string): Promise<ReportContentResponse> {
    const { data } = await apiClient.get<ReportContentResponse>(
      `/auth/admin/llm-benchmarks/${runId}/${slug}`
    );
    return data;
  },

  /** Fetch the JSON sidecar (CompareResult dump). Throws when the
   *  sidecar is missing (older runs that pre-date the sidecar emit
   *  return 404 here — callers should treat that as "charts unavailable",
   *  not as a hard error). */
  async getReportData(runId: string, slug: string): Promise<BenchmarkCompareResult> {
    const { data } = await apiClient.get<BenchmarkCompareResult>(
      `/auth/admin/llm-benchmarks/${runId}/${slug}/data`
    );
    return data;
  },
};

// ---------------------------------------------------------------------------
// JSON sidecar shape — mirrors CompareResult / AgentBenchmark in the backend.
// model_dump_json walks the full tree; @property methods on the Python side
// (total_cost_usd, etc.) are NOT in the dump, so we compute aggregates here.
// ---------------------------------------------------------------------------

export interface BenchmarkConfig {
  config_id: string;
  provider: string | null;
  model: string | null;
}

export interface BenchmarkScorecardEntry {
  name: string;
  value: unknown;
  passed: boolean;
  notes: string;
}

export interface BenchmarkScorecard {
  agent: string;
  entries: BenchmarkScorecardEntry[];
  /** Optional pre-computed pass rate. Set on backfilled records
   *  (so we don't have to inflate the entries array to preserve 3-decimal
   *  precision). Native CompareResult dumps don't set this — pass rate
   *  is computed from entries.passed / entries.length in that case. */
  overall_pass_rate?: number | null;
}

export interface BenchmarkLLMCallMetric {
  model: string;
  input_tokens: number;
  output_tokens: number;
  elapsed_seconds: number;
  cost_usd: number;
}

export interface BenchmarkAgentRun {
  agent: string;
  config_id: string;
  fixture_id: string;
  seed: number;
  input_payload: Record<string, unknown>;
  output_payload: unknown;
  raised: string | null;
  llm_calls: BenchmarkLLMCallMetric[];
  scorecard: BenchmarkScorecard | null;
}

export interface BenchmarkCompareResult {
  fixture_id: string;
  fixture_label: string;
  configs: BenchmarkConfig[];
  agents: string[];
  /** runs[config_id][agent] = array of AgentBenchmark (one per seed). */
  runs: Record<string, Record<string, BenchmarkAgentRun[]>>;
}
